import { useState, useEffect, useCallback } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { List, MapTrifold, MapPin } from "@phosphor-icons/react";
import { useAuth } from "./contexts/AuthContext";
import Header from "./components/Header";
import FilterBar from "./components/FilterBar";
import EventList from "./components/EventList";
import MapView from "./components/MapView";
import EventModal from "./components/EventModal";
import AuthCallback from "./components/AuthCallback";
import MyShows from "./components/MyShows";
import SubmitEvent from "./components/SubmitEvent";
import SEOStructuredData from "./components/SEOStructuredData";
import VersionCheck from "./components/VersionCheck";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MainPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [states, setStates] = useState([]);
  const [categories] = useState(["festival", "fair", "community"]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [mobileView, setMobileView] = useState("list");
  
  // Near Me state
  const [nearMeActive, setNearMeActive] = useState(false);
  const [nearMeRadius, setNearMeRadius] = useState(50);
  const [userLocation, setUserLocation] = useState(null);
  const [nearMeLoading, setNearMeLoading] = useState(false);
  const [nearMeEvents, setNearMeEvents] = useState([]);
  
  // Map visibility state
  const [mapVisible, setMapVisible] = useState(true);
  const [mapMinimized, setMapMinimized] = useState(false);

  // Saved shows
  const [savedShowIds, setSavedShowIds] = useState(new Set());

  // Auto-detect location via IP geolocation (no permission needed)
  const [autoLocation, setAutoLocation] = useState(null);
  const [ipLocationLoaded, setIpLocationLoaded] = useState(false);
  const [defaultNearbyEvents, setDefaultNearbyEvents] = useState(null);
  
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/geolocate`);
        const { lat, lng, city, state } = res.data;
        if (lat && lng) {
          setAutoLocation({ lat, lng, city, state });
          // Try 50 miles first, expand to 100 if too few
          let nearbyRes = await axios.get(`${API}/events/nearby`, {
            params: { lat, lng, radius: 50 }
          });
          let nearby = nearbyRes.data || [];
          if (nearby.length < 5) {
            nearbyRes = await axios.get(`${API}/events/nearby`, {
              params: { lat, lng, radius: 100 }
            });
            nearby = nearbyRes.data || [];
          }
          setDefaultNearbyEvents(nearby.length > 0 ? nearby : []);
        }
      } catch (error) {
        console.error("IP geolocation failed:", error.message);
      } finally {
        setIpLocationLoaded(true);
      }
    })();
  }, []);
  
  // Filter states
  const [filters, setFilters] = useState({
    category: "",
    state: "",
    search: "",
    startDate: null,
    endDate: null,
    applicationsOpen: false,
    boothFeeRange: "",
    attendanceRange: ""
  });

  // Fetch saved show IDs when user logs in
  useEffect(() => {
    if (!user) {
      setSavedShowIds(new Set());
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/saved-shows`, { withCredentials: true });
        setSavedShowIds(new Set(res.data.saved_shows || []));
      } catch (error) {
        console.error("Failed to load saved shows:", error.message);
      }
    })();
  }, [user]);

  const toggleSaveShow = useCallback(async (eventId) => {
    if (!user) {
      toast.info("Sign in to save shows");
      return;
    }
    const isSaved = savedShowIds.has(eventId);
    // Optimistic update
    setSavedShowIds(prev => {
      const next = new Set(prev);
      if (isSaved) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
    try {
      if (isSaved) {
        await axios.delete(`${API}/auth/saved-shows/${eventId}`, { withCredentials: true });
        toast.success("Show removed from saved");
      } else {
        await axios.post(`${API}/auth/saved-shows/${eventId}`, {}, { withCredentials: true });
        toast.success("Show saved!");
      }
    } catch (error) {
      console.error("Failed to update saved shows:", error.message);
      // Revert on error
      setSavedShowIds(prev => {
        const next = new Set(prev);
        if (isSaved) next.add(eventId);
        else next.delete(eventId);
        return next;
      });
      toast.error("Failed to update saved shows");
    }
  }, [user, savedShowIds]);

  // Fetch events with abort controller to prevent race conditions
  const fetchEvents = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append("category", filters.category);
      if (filters.state) params.append("state", filters.state);
      if (filters.search) params.append("search", filters.search);
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      if (filters.applicationsOpen) params.append("applications_open", "true");
      if (filters.boothFeeRange) params.append("booth_fee_range", filters.boothFeeRange);
      if (filters.attendanceRange) params.append("attendance_range", filters.attendanceRange);
      
      // Pass location for proximity sorting when no state filter is active
      if (autoLocation && !filters.state) {
        params.append("lat", autoLocation.lat);
        params.append("lng", autoLocation.lng);
      }
      
      const response = await axios.get(`${API}/events?${params.toString()}`, { signal });
      setEvents(response.data);
      setFilteredEvents(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error("Error fetching events:", error);
      toast.error("Failed to load shows. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters, autoLocation]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/states`);
      setStates(response.data.states);
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  const handleRefreshEvents = async () => {
    setRefreshing(true);
    try {
      toast.info("Searching for new shows...");
      await axios.post(`${API}/scrape-events`);
      let attempts = 0;
      const checkStatus = async () => {
        const statusRes = await axios.get(`${API}/scrape-status`);
        if (statusRes.data.status === "idle" || attempts > 30) {
          setRefreshing(false);
          await fetchEvents();
          await fetchStates();
          if (statusRes.data.events_added > 0) {
            toast.success(`Found ${statusRes.data.events_added} new shows!`);
          } else {
            toast.info("Shows are up to date.");
          }
        } else {
          attempts++;
          setTimeout(checkStatus, 2000);
        }
      };
      setTimeout(checkStatus, 3000);
    } catch (error) {
      console.error("Error refreshing events:", error);
      toast.error("Failed to refresh shows.");
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchEvents(controller.signal);
    return () => controller.abort();
  }, [fetchEvents]);

  useEffect(() => { fetchStates(); }, []);

  useEffect(() => {
    const interval = setInterval(() => { fetchEvents(); }, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleClearFilters = () => {
    setFilters({
      category: "", state: "", search: "", startDate: null, endDate: null,
      applicationsOpen: false, boothFeeRange: "", attendanceRange: ""
    });
    setDefaultNearbyEvents([]); // Clear nearby default to show all events
  };

  const handleReturnHome = () => {
    handleClearFilters();
    deactivateNearMe();
    setIsModalOpen(false);
    setSelectedEvent(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const handleMarkerClick = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  // Near Me functions
  const fetchNearbyEvents = useCallback(async (loc, radius) => {
    setNearMeLoading(true);
    try {
      const response = await axios.get(`${API}/events/nearby`, {
        params: { lat: loc.lat, lng: loc.lng, radius }
      });
      setNearMeEvents(response.data);
    } catch (error) {
      console.error("Failed to find nearby events:", error.message);
      toast.error("Failed to find nearby events");
    } finally {
      setNearMeLoading(false);
    }
  }, []);

  const activateNearMe = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setNearMeLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
        setUserLocation(loc);
        setNearMeActive(true);
        fetchNearbyEvents(loc, nearMeRadius);
      },
      () => {
        setNearMeLoading(false);
        toast.error("Unable to get your location. Please allow location access.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [nearMeRadius, fetchNearbyEvents]);

  const handleRadiusChange = useCallback((newRadius) => {
    setNearMeRadius(newRadius);
    if (userLocation) fetchNearbyEvents(userLocation, newRadius);
  }, [userLocation, fetchNearbyEvents]);

  const deactivateNearMe = useCallback(() => {
    setNearMeActive(false);
    setNearMeEvents([]);
    setUserLocation(null);
  }, []);

  const hasActiveFilters = filters.category || filters.state || filters.search || 
    filters.startDate || filters.endDate || filters.applicationsOpen || 
    filters.boothFeeRange || filters.attendanceRange;
  
  const displayEvents = nearMeActive 
    ? nearMeEvents 
    : (hasActiveFilters || defaultNearbyEvents === null || defaultNearbyEvents.length === 0)
      ? filteredEvents 
      : defaultNearbyEvents;

  const handleEventUpdate = (eventId, newRating, newRatingCount) => {
    setEvents(prev => prev.map(e => e.id === eventId ? { ...e, rating: newRating, rating_count: newRatingCount } : e));
    setFilteredEvents(prev => prev.map(e => e.id === eventId ? { ...e, rating: newRating, rating_count: newRatingCount } : e));
    if (selectedEvent && selectedEvent.id === eventId) {
      setSelectedEvent(prev => ({ ...prev, rating: newRating, rating_count: newRatingCount }));
    }
  };

  return (
    <>
      <Header 
        onRefresh={handleRefreshEvents}
        refreshing={refreshing}
        lastUpdated={lastUpdated}
        onReturnHome={handleReturnHome}
      />
      
      <main className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1800px] mx-auto">
        <FilterBar 
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          states={states}
          categories={categories}
          nearMeActive={nearMeActive}
          nearMeLoading={nearMeLoading}
          nearMeRadius={nearMeRadius}
          onActivateNearMe={activateNearMe}
          onDeactivateNearMe={deactivateNearMe}
          onRadiusChange={handleRadiusChange}
          nearMeCount={nearMeEvents.length}
        />

        {/* Location-based default indicator */}
        {!hasActiveFilters && !nearMeActive && autoLocation?.city && defaultNearbyEvents && defaultNearbyEvents.length > 0 && (
          <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#81B29A]/10 border border-[#81B29A]/20 rounded-lg" data-testid="location-banner">
            <MapPin weight="fill" className="w-4 h-4 text-[#81B29A] flex-shrink-0" />
            <span className="text-sm text-[#2B2D42]">
              Showing <strong>{defaultNearbyEvents.length}</strong> events near <strong>{autoLocation.city}, {autoLocation.state}</strong>
            </span>
            <button
              onClick={handleClearFilters}
              className="ml-auto text-xs text-[#6C757D] hover:text-[#E07A5F] underline"
              data-testid="show-all-events-btn"
            >
              Show all events
            </button>
          </div>
        )}
        
        {/* Mobile View Toggle */}
        <div className="flex lg:hidden items-center gap-2 mt-4" data-testid="mobile-view-toggle">
          <button
            onClick={() => setMobileView("list")}
            className={`view-toggle-btn flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border border-[#D6D3D1] ${mobileView === "list" ? "active" : ""}`}
            data-testid="toggle-list-view"
          >
            <List weight="bold" className="w-4 h-4" />
            List View
          </button>
          <button
            onClick={() => { setMobileView("map"); setMapVisible(true); setMapMinimized(false); }}
            className={`view-toggle-btn flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border border-[#D6D3D1] ${mobileView === "map" ? "active" : ""}`}
            data-testid="toggle-map-view"
          >
            <MapTrifold weight="bold" className="w-4 h-4" />
            Map View
          </button>
        </div>

        {/* Mobile: Show one view at a time */}
        <div className="block lg:hidden mt-4">
          {mobileView === "map" ? (
            <MapView 
              events={displayEvents}
              onMarkerClick={handleMarkerClick}
              selectedEvent={selectedEvent}
              userLocation={userLocation}
              nearMeActive={nearMeActive}
              nearMeRadius={nearMeRadius}
              isVisible={true}
              onClose={() => setMobileView("list")}
              isMinimized={mapMinimized}
              onToggleMinimize={() => setMapMinimized(prev => !prev)}
            />
          ) : (
            <EventList 
              events={displayEvents}
              loading={nearMeActive ? nearMeLoading : loading}
              onEventClick={handleEventClick}
              selectedEvent={selectedEvent}
              nearMeActive={nearMeActive}
              savedShowIds={savedShowIds}
              onToggleSave={toggleSaveShow}
            />
          )}
        </div>

        {/* Desktop/Tablet: Side by side or full-width list */}
        <div className={`hidden lg:flex gap-6 mt-6 ${mapMinimized && mapVisible ? "flex-col" : ""}`}>
          {mapVisible && (
            <div className={`transition-all duration-300 flex-shrink-0 ${mapMinimized ? "w-full" : "w-5/12"}`}>
              <MapView 
                events={displayEvents}
                onMarkerClick={handleMarkerClick}
                selectedEvent={selectedEvent}
                userLocation={userLocation}
                nearMeActive={nearMeActive}
                nearMeRadius={nearMeRadius}
                isVisible={mapVisible}
                onClose={() => setMapVisible(false)}
                isMinimized={mapMinimized}
                onToggleMinimize={() => setMapMinimized(prev => !prev)}
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <EventList 
              events={displayEvents}
              loading={nearMeActive ? nearMeLoading : loading}
              onEventClick={handleEventClick}
              selectedEvent={selectedEvent}
              nearMeActive={nearMeActive}
              savedShowIds={savedShowIds}
              onToggleSave={toggleSaveShow}
            />
          </div>
        </div>

        {!mapVisible && (
          <button
            onClick={() => { setMapVisible(true); setMapMinimized(false); }}
            className="hidden lg:flex fixed bottom-6 left-6 z-50 items-center gap-2 px-4 py-3 bg-[#E07A5F] hover:bg-[#D46A50] text-white rounded-full shadow-lg hover:shadow-xl transition-all"
            data-testid="map-reopen-btn"
          >
            <MapTrifold weight="duotone" className="w-5 h-5" />
            <span className="text-sm font-medium">Show Map</span>
          </button>
        )}
      </main>
      
      <EventModal 
        event={selectedEvent}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        isSaved={selectedEvent ? savedShowIds.has(selectedEvent.id) : false}
        onToggleSave={toggleSaveShow}
      />
    </>
  );
}

function App() {
  const location = useLocation();

  // CRITICAL: Detect session_id during render (NOT in useEffect) to prevent race conditions
  if (location.hash?.includes("session_id=")) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]" data-testid="app-container">
        <Toaster position="top-right" richColors />
        <AuthCallback />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="app-container">
      <SEOStructuredData />
      <VersionCheck />
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/my-shows" element={<MyShowsPage />} />
        <Route path="/submit-event" element={<SubmitEventPage />} />
        <Route path="*" element={<MainPage />} />
      </Routes>
    </div>
  );
}

function MyShowsPage() {
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useAuth();
  const [savedShowIds, setSavedShowIds] = useState(new Set());

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/saved-shows`, { withCredentials: true });
        setSavedShowIds(new Set(res.data.saved_shows || []));
      } catch (error) { console.error("Failed to load saved shows:", error.message); }
    })();
  }, [user]);

  const toggleSaveShow = useCallback(async (eventId) => {
    if (!user) return;
    const isSaved = savedShowIds.has(eventId);
    setSavedShowIds(prev => {
      const next = new Set(prev);
      if (isSaved) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
    try {
      if (isSaved) await axios.delete(`${API}/auth/saved-shows/${eventId}`, { withCredentials: true });
      else await axios.post(`${API}/auth/saved-shows/${eventId}`, {}, { withCredentials: true });
    } catch (error) { console.error("Failed to toggle saved show:", error.message); }
  }, [user, savedShowIds]);

  return (
    <>
      <Header
        onRefresh={() => {}}
        refreshing={false}
        lastUpdated={null}
        onReturnHome={() => navigate("/")}
      />
      <main className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1800px] mx-auto">
        <MyShows
          onViewEvent={(ev) => { setSelectedEvent(ev); setIsModalOpen(true); }}
        />
      </main>
      <EventModal
        event={selectedEvent}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedEvent(null); }}
        isSaved={selectedEvent ? savedShowIds.has(selectedEvent.id) : false}
        onToggleSave={toggleSaveShow}
      />
    </>
  );
}

function SubmitEventPage() {
  const navigate = useNavigate();
  return (
    <>
      <Header onRefresh={() => {}} refreshing={false} lastUpdated={null} onReturnHome={() => navigate("/")} />
      <main className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1800px] mx-auto">
        <SubmitEvent />
      </main>
    </>
  );
}

export default App;
